import { useRef } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { googleBewertungen, googleProfilUrl, hatEchteBewertungen } from "../data/bewertungen";

function ReviewCard({ r, i }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };
  return (
    <motion.figure
      ref={ref}
      onMouseMove={onMove}
      data-testid={`review-card-${i}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ delay: i * 0.18, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className={`relative overflow-hidden rounded-2xl border border-[#D4AF37]/15 bg-[#121212]/60 p-8 backdrop-blur-2xl shadow-[0_8px_32px_rgba(212,175,55,0.05)] ${
        i === 1 ? "md:-translate-y-10 md:border-[#D4AF37]/30" : ""
      }`}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 hover:opacity-100"
        style={{
          background:
            "radial-gradient(320px at var(--mx, 50%) var(--my, 50%), rgba(212,175,55,0.12), transparent 65%)",
        }}
      />
      <span className="gold-text font-serif-accent text-6xl leading-none">&ldquo;</span>
      <div className="mb-5 mt-2 flex gap-1" aria-label={`${r.stars ?? 5} von 5 Sternen`}>
        {Array.from({ length: 5 }).map((_, s) => (
          <Star
            key={s}
            size={14}
            className={
              s < (r.stars ?? 5)
                ? "fill-[#D4AF37] text-[#D4AF37]"
                : "text-[#D4AF37]/25"
            }
          />
        ))}
      </div>
      <blockquote className="text-base font-light leading-relaxed text-[#F5F5F0] md:text-lg">
        {r.quote}
      </blockquote>
      <figcaption className="mt-8 border-t border-white/5 pt-5">
        <p className="font-display text-base font-bold tracking-wide">{r.author}</p>
        <p className="mt-1 text-xs tracking-[0.2em] text-[#BDBDB6]">{r.role.toUpperCase()}</p>
      </figcaption>
    </motion.figure>
  );
}

export default function Testimonials() {
  return (
    <section id="bewertungen" data-testid="testimonials-section" className="relative z-10 px-6 py-28 md:px-14 md:py-40">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-24 text-center"
        >
          <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">BEWERTUNGEN</p>
          <h2 className="mx-auto max-w-3xl font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            {hatEchteBewertungen ? (
              <>
                Stimmen, die{" "}
                <em className="gold-text font-serif-accent normal-case italic">überzeugen.</em>
              </>
            ) : (
              <>
                Bewertungen, die{" "}
                <em className="gold-text font-serif-accent normal-case italic">echt sind.</em>
              </>
            )}
          </h2>
        </motion.div>
        {hatEchteBewertungen ? (
          <div className="grid gap-8 md:grid-cols-3">
            {googleBewertungen.map((r, i) => (
              <ReviewCard key={r.author} r={r} i={i} />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="reviews-empty"
            className="mx-auto max-w-2xl rounded-2xl border border-[#D4AF37]/15 bg-[#121212]/60 p-10 text-center backdrop-blur-2xl md:p-14"
          >
            <p className="text-base font-light leading-relaxed text-[#F5F5F0] md:text-lg">
              Hier stehen ausschließlich echte Bewertungen unserer Kundinnen und Kunden — im
              Wortlaut, ohne Auswahl. Die ersten sammeln wir gerade.
            </p>
            <p className="mt-5 text-sm font-light leading-relaxed text-[#BDBDB6]">
              Bis dahin überzeugen wir lieber mit Arbeit als mit Zitaten: Die Projekte weiter oben
              zeigen, wie wir gestalten.
            </p>
          </motion.div>
        )}

        {googleProfilUrl && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12 text-center"
          >
            <a
              href={googleProfilUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="text-sm tracking-[0.25em] text-[#BDBDB6] underline-offset-8 transition-colors duration-300 hover:text-[#D4AF37] hover:underline"
            >
              {hatEchteBewertungen ? "ALLE BEWERTUNGEN BEI GOOGLE ANSEHEN" : "UNSER GOOGLE-PROFIL"}
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}
